/**
 * Hook pour tracker le watch time des vidéos/reels
 * Envoie les données au backend pour l'algorithme trending
 */

import { useRef, useCallback, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function useWatchTimeTracker(reelId, videoDuration = 30) {
  const startTimeRef = useRef(null);
  const watchTimeRef = useRef(0);
  const hasReplayedRef = useRef(false);
  const hasCompletedRef = useRef(false);
  const hasSentRef = useRef(false);
  
  // Démarrer le tracking quand la vidéo commence
  const startTracking = useCallback(() => {
    startTimeRef.current = Date.now();
  }, []);
  
  // Mettre en pause le tracking
  const pauseTracking = useCallback(() => {
    if (startTimeRef.current) {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      watchTimeRef.current += elapsed;
      startTimeRef.current = null;
    }
  }, []);
  
  // Marquer comme replay
  const markReplay = useCallback(() => {
    hasReplayedRef.current = true;
  }, []);
  
  // Marquer comme complété
  const markCompleted = useCallback(() => {
    hasCompletedRef.current = true;
  }, []);
  
  // Envoyer les données au backend
  const sendViewData = useCallback(async () => {
    if (hasSentRef.current || !reelId) return;
    
    // Arrêter le timer si en cours
    if (startTimeRef.current) {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      watchTimeRef.current += elapsed;
      startTimeRef.current = null;
    }
    
    // Ne pas envoyer si moins de 1 seconde de visionnage
    if (watchTimeRef.current < 1) return;
    
    hasSentRef.current = true;
    
    try {
      await fetch(`${API_URL}/api/reseau/trending/reels/${reelId}/view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: localStorage.getItem('user_id') || 'anonymous',
          watch_time_seconds: watchTimeRef.current,
          video_duration_seconds: videoDuration,
          completed: hasCompletedRef.current,
          replayed: hasReplayedRef.current,
          device_type: window.innerWidth < 768 ? 'mobile' : 'desktop'
        })
      });
    } catch (err) {
      console.warn('Failed to track reel view:', err);
    }
  }, [reelId, videoDuration]);
  
  // Reset pour un nouveau reel
  const resetTracking = useCallback(() => {
    startTimeRef.current = null;
    watchTimeRef.current = 0;
    hasReplayedRef.current = false;
    hasCompletedRef.current = false;
    hasSentRef.current = false;
  }, []);
  
  // Envoyer les données quand le composant se démonte
  useEffect(() => {
    return () => {
      sendViewData();
    };
  }, [sendViewData]);
  
  return {
    startTracking,
    pauseTracking,
    markReplay,
    markCompleted,
    sendViewData,
    resetTracking,
    getWatchTime: () => {
      let total = watchTimeRef.current;
      if (startTimeRef.current) {
        total += (Date.now() - startTimeRef.current) / 1000;
      }
      return total;
    }
  };
}

/**
 * Hook pour tracker l'engagement sur les posts
 */
export function usePostEngagementTracker(postId) {
  const startTimeRef = useRef(null);
  const hasSentViewRef = useRef(false);
  
  // Commencer à tracker quand le post devient visible
  const startViewing = useCallback(() => {
    if (!startTimeRef.current) {
      startTimeRef.current = Date.now();
    }
  }, []);
  
  // Envoyer l'événement de vue
  const sendViewEvent = useCallback(async () => {
    if (hasSentViewRef.current || !postId || !startTimeRef.current) return;
    
    const timeSpent = (Date.now() - startTimeRef.current) / 1000;
    
    // Ne pas envoyer si moins de 2 secondes (scroll rapide)
    if (timeSpent < 2) return;
    
    hasSentViewRef.current = true;
    
    try {
      await fetch(`${API_URL}/api/reseau/trending/posts/${postId}/engagement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: localStorage.getItem('user_id') || 'anonymous',
          action: 'view',
          time_spent_seconds: timeSpent
        })
      });
    } catch (err) {
      console.warn('Failed to track post view:', err);
    }
  }, [postId]);
  
  // Tracker une action (like, comment, share)
  const trackAction = useCallback(async (action) => {
    if (!postId) return;
    
    try {
      await fetch(`${API_URL}/api/reseau/trending/posts/${postId}/engagement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: localStorage.getItem('user_id') || 'anonymous',
          action: action,
          time_spent_seconds: startTimeRef.current ? (Date.now() - startTimeRef.current) / 1000 : 0
        })
      });
    } catch (err) {
      console.warn('Failed to track post action:', err);
    }
  }, [postId]);
  
  // Envoyer la vue quand le composant se démonte
  useEffect(() => {
    return () => {
      sendViewEvent();
    };
  }, [sendViewEvent]);
  
  return {
    startViewing,
    sendViewEvent,
    trackAction
  };
}

export default useWatchTimeTracker;
